<template>
    <div class="gate-odol">
        <div class="row">
            <div
                v-for="item in trafficCards"
                :key="item.key"
                class="col-12 col-md-6 mb-3"
            >
                <div class="traffic-card" :class="`traffic-card--${item.key}`">
                    <div class="traffic-card__header">
                        <div>
                            <p class="traffic-card__label">{{ item.title }}</p>
                            <h3 class="traffic-card__total">{{ item.total }}</h3>
                        </div>
                        <div class="traffic-card__badge">Kendaraan</div>
                    </div>

                    <div class="traffic-card__stats">
                        <div
                            v-for="category in item.categories"
                            :key="category.label"
                            class="traffic-card__stat"
                        >
                            <span class="traffic-card__stat-label">{{ category.label }}</span>
                            <strong class="traffic-card__stat-value">{{ category.count }}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div
                v-for="item in topVehicleCards"
                :key="item.key"
                class="col-12 col-md-6 mb-3"
            >
                <div class="traffic-list-card" :class="`traffic-list-card--${item.key}`">
                    <div class="traffic-list-card__header">
                        <p class="traffic-list-card__label">{{ item.title }}</p>
                        <span class="traffic-list-card__chip">Top 10</span>
                    </div>

                    <div v-if="item.rows.length" class="traffic-list-card__body">
                        <div
                            v-for="(vehicle, index) in item.rows"
                            :key="`${item.key}-${vehicle.plate}-${index}`"
                            class="traffic-list-card__row"
                        >
                            <span class="traffic-list-card__rank">{{ index + 1 }}</span>
                            <span class="traffic-list-card__plate">{{ vehicle.plate }}</span>
                            <strong class="traffic-list-card__count">{{ formatNumber(vehicle.count) }}</strong>
                        </div>
                    </div>

                    <p v-else class="traffic-list-card__empty">Belum ada data kendaraan.</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const defaultGateOdolData = () => ({
    in: {
        total: 0,
        categories: {
            umum: 0,
            container: 0,
            looseCargo: 0,
        },
    },
    out: {
        total: 0,
        categories: {
            umum: 0,
            container: 0,
            looseCargo: 0,
        },
    },
    top10: {
        in: [],
        out: [],
    },
});

export default {
    name: 'GateOdol',
    props: {
        gateOdolData: {
            type: Object,
            default: defaultGateOdolData,
        },
    },
    computed: {
        trafficCards() {
            const gateOdolData = this.gateOdolData || defaultGateOdolData();

            return [
                {
                    key: 'in',
                    title: 'Kendaraan Masuk',
                    total: this.getTotalCount(gateOdolData.in),
                    categories: [
                        { label: 'Umum', count: this.getCategoryCount(gateOdolData.in, 'umum') },
                        { label: 'Container', count: this.getCategoryCount(gateOdolData.in, 'container') },
                        { label: 'Loose Cargo', count: this.getCategoryCount(gateOdolData.in, 'looseCargo') },
                    ],
                },
                {
                    key: 'out',
                    title: 'Kendaraan Keluar',
                    total: this.getTotalCount(gateOdolData.out),
                    categories: [
                        { label: 'Umum', count: this.getCategoryCount(gateOdolData.out, 'umum') },
                        { label: 'Container', count: this.getCategoryCount(gateOdolData.out, 'container') },
                        { label: 'Loose Cargo', count: this.getCategoryCount(gateOdolData.out, 'looseCargo') },
                    ],
                },
            ];
        },
        topVehicleCards() {
            const gateOdolData = this.gateOdolData || defaultGateOdolData();

            return [
                {
                    key: 'in',
                    title: 'Top 10 Kendaraan Masuk',
                    rows: this.extractTopVehicles(gateOdolData, 'in'),
                },
                {
                    key: 'out',
                    title: 'Top 10 Kendaraan Keluar',
                    rows: this.extractTopVehicles(gateOdolData, 'out'),
                },
            ];
        },
    },
    methods: {
        formatNumber(value) {
            return (Number(value) || 0).toLocaleString('id-ID');
        },
        getTotalCount(section = {}) {
            return Number(section.total) || 0;
        },
        getCategoryCount(section = {}, categoryKey) {
            const categories = section.categories;

            if (Array.isArray(categories)) {
                const found = categories.find((item) => Object.prototype.hasOwnProperty.call(item, categoryKey));
                return found ? Number(found[categoryKey]) || 0 : 0;
            }

            if (categories && typeof categories === 'object') {
                return Number(categories[categoryKey]) || 0;
            }

            return 0;
        },
        extractTopVehicles(payload = {}, direction = 'in') {
            const list =
                payload?.top10?.[direction] ||
                payload?.[`top10${direction.toUpperCase()}`] ||
                payload?.[`top_10_${direction}`] ||
                payload?.[`top_${direction}`] ||
                payload?.[`${direction}_top_10`] ||
                payload?.[direction]?.top10 ||
                payload?.[direction]?.top ||
                payload?.[direction]?.vehicles ||
                [];

            if (!Array.isArray(list)) {
                return [];
            }

            return list
                .map((item, index) => {
                    if (typeof item === 'string') {
                        return { plate: item, count: 0 };
                    }

                    if (!item || typeof item !== 'object') {
                        return null;
                    }

                    const plate =
                        item.no_pol ||
                        item.lic_plate ||
                        item.license_plate ||
                        item.number_plate ||
                        item.plate ||
                        item.vehicle ||
                        `Kendaraan ${index + 1}`;

                    const count =
                        item.total ||
                        item.count ||
                        item.intensity ||
                        item.frequency ||
                        item.trip ||
                        item.value ||
                        0;

                    return {
                        plate,
                        count: Number(count) || 0,
                    };
                })
                .filter((item) => !!item)
                .slice(0, 10);
        },
    },
};
</script>

<style scoped>
.gate-odol {
    padding: 0.25rem;
}

.traffic-card {
    height: 100%;
    padding: 1.25rem;
    border-radius: 16px;
    color: #fff;
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.14);
}

.traffic-card--in {
    background: linear-gradient(135deg, #0f766e, #14b8a6);
}

.traffic-card--out {
    background: linear-gradient(135deg, #1d4ed8, #60a5fa);
}

.traffic-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.traffic-card__label {
    margin-bottom: 0.35rem;
    font-size: 0.95rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.85;
}

.traffic-card__total {
    margin: 0;
    font-size: 2.25rem;
    font-weight: 700;
    line-height: 1;
}

.traffic-card__badge {
    padding: 0.45rem 0.8rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.traffic-card__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
}

.traffic-card__stat {
    padding: 0.9rem 0.8rem;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.14);
}

.traffic-card__stat-label {
    display: block;
    margin-bottom: 0.35rem;
    font-size: 0.82rem;
    opacity: 0.85;
}

.traffic-card__stat-value {
    font-size: 1.35rem;
    line-height: 1;
}

.traffic-list-card {
    height: 100%;
    padding: 1rem 1.1rem;
    border-radius: 14px;
    border: 1px solid #d9e3f1;
    background: linear-gradient(180deg, #ffffff, #f8fbff);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.traffic-list-card--in {
    border-top: 4px solid #14b8a6;
}

.traffic-list-card--out {
    border-top: 4px solid #60a5fa;
}

.traffic-list-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
    margin-bottom: 0.9rem;
}

.traffic-list-card__label {
    margin: 0;
    color: #0f172a;
    font-size: 0.98rem;
    font-weight: 700;
}

.traffic-list-card__chip {
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    background: #eef2ff;
    color: #1e3a8a;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.03em;
}

.traffic-list-card__body {
    display: grid;
    gap: 0.5rem;
}

.traffic-list-card__row {
    display: grid;
    grid-template-columns: 28px 1fr auto;
    align-items: center;
    gap: 0.65rem;
    padding: 0.48rem 0.55rem;
    border-radius: 9px;
    background: #ffffff;
    border: 1px solid #ecf2fb;
}

.traffic-list-card__rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #f1f5f9;
    color: #334155;
    font-size: 0.78rem;
    font-weight: 700;
}

.traffic-list-card__plate {
    color: #0f172a;
    font-size: 0.88rem;
    font-weight: 600;
}

.traffic-list-card__count {
    color: #0369a1;
    font-size: 0.9rem;
}

.traffic-list-card__empty {
    margin: 0;
    color: #64748b;
    font-size: 0.9rem;
    text-align: center;
    padding: 1rem 0.5rem;
}

@media (max-width: 767.98px) {
    .traffic-card__stats {
        grid-template-columns: 1fr;
    }
}
</style>